import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { addWorkerSkills, fetchWorkerProfile } from "../../../../../../../../export/workers";
import { resetAddWorkerSkills } from "../../../../../../../../export/workers";
import LoadingButton from "../../../../../../../design/wilya-loading-button";
import useWorkersData from "../../../../../controllers/use-workers-data";

interface Skill {
	skillId: number;
	levelId: number | string;
	workerId: string;
}
interface ActionButtonsProps {
	closeDialog?: () => void;
	status: string;
	newAssignedSkills?: Skill[];
	onCombinedResult?: (result: { message: React.ReactNode; severity: "success" | "error" | "info" }) => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
	closeDialog,
	status,
	newAssignedSkills,
	onCombinedResult,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { allSkills, workerId } = useWorkersData();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const skillsToAdd = newAssignedSkills?.filter((skill) => skill.levelId !== "") || [];
	const skillsCount = skillsToAdd.length;
	const additionMessage =
		skillsCount > 0
			? skillsCount > 1
				? t("workers_skills-selected-for-addition", { count: skillsCount })
				: t("workers_skill-selected-for-addition", { count: skillsCount })
			: "";

	const handleAdd = async () => {
		setIsSubmitting(true);
		dispatch(resetAddWorkerSkills());

		try {
			if (skillsToAdd.length > 0) {
				await dispatch(addWorkerSkills(skillsToAdd) as any);
				const names = skillsToAdd.map(
					(skill) => allSkills.find((s: any) => s.id === skill.skillId)?.name || `Skill ${skill.skillId}`
				);

				if (onCombinedResult) {
					onCombinedResult({
						message:
							names.length > 1
								? t("workers_skills-added-success")
								: t("workers_skill-added-success"),
						severity: "success",
					});
				}

				if (workerId) {
					dispatch(fetchWorkerProfile(workerId));
				}
			}

			setIsSubmitting(false);
			if (closeDialog) {
				setTimeout(() => {
					closeDialog();
				}, 500);
			}
		} catch (error) {
			console.error("Error adding skills", error);
			if (onCombinedResult) {
				onCombinedResult({
					message: t("workers_error-adding-skills"),
					severity: "error",
				});
			}
			setIsSubmitting(false);
			if (closeDialog) {
				setTimeout(() => {
					closeDialog();
				}, 500);
			}
		}
	};

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					padding: "32px 0px 45px 0px",
				}}
			>
				<Box>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontSize: "16px",
							fontWeight: 400,
							color: "#000000DE",
							lineHeight: "24px",
						}}
					>
						{skillsCount > 0 ? additionMessage : ""}
					</Typography>
				</Box>
				<Box>
					<Button variant='outlined' onClick={closeDialog} disabled={isSubmitting}>
						{t("common_cancel")}
					</Button>
					<LoadingButton
						variant='contained'
						disabled={skillsCount === 0}
						sx={{ marginLeft: "16px", minWidth: "100px" }}
						loading={status === "pending" || isSubmitting}
						type='submit'
						onClick={handleAdd}
						loadingIndicator={
							<Box display='flex'>
								<CircularProgress
									size={16}
									sx={{ color: "rgba(0, 0, 0, 0.38)" }}
								/>
								<Typography variant='body2' sx={{ marginLeft: "8px" }}>
									{t("workers_adding")}
								</Typography>
							</Box>
						}
					>
						{t("common_add")}
					</LoadingButton>
				</Box>
			</Box>
		</>
	);
};

export default ActionButtons;
